"use client";

import { useState } from "react";
import {
  CipherGrid,
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import { Checkbox, Notice, TextField } from "@/components/tool-kit/primitives";
import { HINT, LABEL } from "@/components/tool-kit/styles";
import { KeyStream } from "@/components/tools/ciphers-polygraphic/key-stream";
import { SquareOptions } from "@/components/tools/ciphers-polygraphic/square-options";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { groupLetters } from "@/lib/tool-kit/ciphers/alphabet";
import {
  type BifidBlock,
  bifidTransform,
} from "@/lib/tool-kit/ciphers-polygraphic/bifid";
import type {
  GridAlphabet,
  SquareFill,
} from "@/lib/tool-kit/ciphers-polygraphic/squares";

const BLOCK_LIMIT = 4;
const MAX_PERIOD = 99;

/** English strings. A translated content file overrides these through `ui`. */
export const BIFID_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "Odd periods are popular",
  keyword: "Keyword",
  keywordSample: "EXTRAORDINARY",
  keywordHint:
    "Its letters (and digits, in the 6 × 6 square) fill the square first, each used once; the rest of the alphabet follows in order. Paste all 25 or 36 characters to type the square out yourself.",
  kind: "The square",
  mergeIJ: "5 × 5, I and J share a cell",
  dropQ: "5 × 5, leave out Q",
  alnum: "6 × 6, letters and digits",
  fill: "Write the keyword into the square",
  fillHint:
    "Along the rows is the usual way. The American Cryptogram Association's example writes it in a clockwise spiral from the top-left corner.",
  fillRows: "Along the rows",
  fillColumns: "Down the columns",
  fillSpiral: "In a clockwise spiral",
  period: "Period",
  periodHint:
    "Letters per block, each enciphered on its own. Leave empty or 0 to treat the whole message as one block, as Delastelle did.",
  periodSample: "7",
  group: "Cipher text in groups of five",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  outputPlaceholder: "The result appears here.",
  letterNote:
    "Only letters (and digits, in the 6 × 6 square) are enciphered. Spaces and punctuation are dropped, and the result is in capitals.",
  ijNote: "I and J share a cell, so every J comes back as I.",
  droppedQ:
    "one: {count} Q was left out — this square has no Q.|other: {count} Qs were left out — this square has no Q.",
  working: "Square and blocks",
  squareLabel: "Key square",
  squareHint: "Rows and columns are numbered from 1.",
  ruleEncode:
    "Encoding: write each plain letter's row number in one line and its column number beneath it. Read the digits across — all the rows, then all the columns — and cut them into pairs. Each pair is the row and column of a cipher letter.",
  ruleDecode:
    "Decoding: turn each cipher letter into its row and column, and write the digits out in pairs as one stream. The first half of the stream is the rows and the second half the columns; going down the two lines gives the plain letters.",
  blockTitle: "Block {number}",
  plainRow: "Plain",
  cipherRow: "Cipher",
  rowRow: "Row",
  columnRow: "Column",
  pairsRow: "Pairs",
  streamLabel: "Digits across",
  blockLimit:
    "one: Showing the first block.|other: Showing the first {count} blocks.",
} as const;

type Strings = typeof BIFID_CIPHER_STRINGS;

function pairsOf(stream: readonly number[]): string[] {
  const pairs: string[] = [];
  for (let index = 0; index < stream.length; index += 2) {
    pairs.push(`${stream[index]}${stream[index + 1]}`);
  }
  return pairs;
}

function BlockWorking({
  block,
  number,
  encoding,
  strings,
}: {
  block: BifidBlock;
  number: string;
  encoding: boolean;
  strings: Strings;
}) {
  const half = block.rows.length;
  const plain = (
    <KeyStream
      key="plain"
      rows={[
        { label: strings.plainRow, letters: [...block.plain] },
        { label: strings.rowRow, letters: block.rows.map(String) },
        { label: strings.columnRow, letters: block.columns.map(String) },
      ]}
      shaded={0}
      title={strings.plainRow}
    />
  );
  const cipher = (
    <KeyStream
      key="cipher"
      rows={[
        { label: strings.pairsRow, letters: pairsOf(block.stream) },
        { label: strings.cipherRow, letters: [...block.cipher] },
      ]}
      shaded={0}
      title={strings.cipherRow}
    />
  );
  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4">
      <p className={LABEL}>{t(strings.blockTitle, { number })}</p>
      {encoding ? [plain, cipher] : [cipher, plain]}
      <p className={HINT}>
        {strings.streamLabel}:{" "}
        <span className="font-mono text-foreground">
          {block.stream.slice(0, half).join("")}{" "}
          {block.stream.slice(half).join("")}
        </span>
      </p>
    </div>
  );
}

export default function BifidCipherWidget() {
  const strings = useToolUi(BIFID_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [keyword, setKeyword] = useState<string>(strings.keywordSample);
  const [kind, setKind] = useState<GridAlphabet>("merge-ij");
  const [fill, setFill] = useState<SquareFill>("spiral");
  const [periodText, setPeriodText] = useState<string>(strings.periodSample);
  const [group, setGroup] = useState(true);

  const encoding = mode === "encode";
  const parsed = parseIntegerInput(periodText);
  const period = Math.min(Math.max(parsed ?? 0, 0), MAX_PERIOD);
  const result = bifidTransform(input, { keyword, kind, fill, period }, mode);
  const output =
    group && encoding ? groupLetters(result.output, 5) : result.output;
  const shown = result.blocks.slice(0, BLOCK_LIMIT);
  const headers = Array.from({ length: result.size }, (_, index) =>
    String(index + 1)
  );

  const keyControls = (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.keywordHint}
          id="bf-keyword"
          label={strings.keyword}
          mono
          onChange={setKeyword}
          value={keyword}
        />
        <TextField
          hint={strings.periodHint}
          id="bf-period"
          label={strings.period}
          mono
          numeric
          onChange={setPeriodText}
          value={periodText}
        />
      </div>
      <SquareOptions
        fill={fill}
        fillHint={strings.fillHint}
        fillLabel={strings.fill}
        fills={[
          { value: "rows", label: strings.fillRows },
          { value: "columns", label: strings.fillColumns },
          { value: "spiral", label: strings.fillSpiral },
        ]}
        idPrefix="bf"
        kind={kind}
        kindLabel={strings.kind}
        kinds={[
          { value: "merge-ij", label: strings.mergeIJ },
          { value: "drop-q", label: strings.dropQ },
          { value: "alnum", label: strings.alnum },
        ]}
        onFillChange={setFill}
        onKindChange={setKind}
      />
      {encoding ? (
        <Checkbox checked={group} label={strings.group} onChange={setGroup} />
      ) : null}
    </div>
  );

  const working = (
    <>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {encoding ? strings.ruleEncode : strings.ruleDecode}
      </p>
      <div className="space-y-2">
        <CipherGrid
          cells={[...result.square]}
          columnHeaders={headers}
          columns={result.size}
          label={strings.squareLabel}
          rowHeaders={headers}
        />
        <p className={HINT}>{strings.squareHint}</p>
      </div>
      {shown.map((block, index) => (
        <BlockWorking
          block={block}
          encoding={encoding}
          // biome-ignore lint/suspicious/noArrayIndexKey: blocks are positional
          key={index}
          number={fmt(index + 1)}
          strings={strings}
        />
      ))}
      {result.blocks.length > BLOCK_LIMIT ? (
        <p className={HINT}>
          {tPlural(strings.blockLimit, locale, BLOCK_LIMIT, {
            count: fmt(BLOCK_LIMIT),
          })}
        </p>
      ) : null}
    </>
  );

  return (
    <CipherWorkbench
      idPrefix="bf"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: encoding ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: encoding ? strings.cipherText : strings.decodedText,
        outputPlaceholder: strings.outputPlaceholder,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={
        result.droppedQ > 0 ? (
          <Notice>
            {tPlural(strings.droppedQ, locale, result.droppedQ, {
              count: fmt(result.droppedQ),
            })}
          </Notice>
        ) : null
      }
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      outputNotes={
        <div className="space-y-1">
          <p className={HINT}>{strings.letterNote}</p>
          {kind === "merge-ij" ? (
            <p className={HINT}>{strings.ijNote}</p>
          ) : null}
        </div>
      }
      working={working}
    />
  );
}
